import { useMemo } from "react";
import { z } from "zod";
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiRequest } from "@/lib/http";
import type { TableParamsApi } from "@/lib/table-params";
import { zSpringPage, zTimestamp } from "./_schemas";

export const connectorSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  type: z.string(),
  status: z.string().nullish(),
  config: z.record(z.string(), z.unknown()).nullish(),
  kind: z.string().nullish(),
  parentId: z.string().nullish(),
  workspaceId: z.string().nullish(),
  archivedAt: zTimestamp.nullish(),
  createdAt: zTimestamp.nullish(),
  updatedAt: zTimestamp.nullish(),
  lastTestedAt: zTimestamp.nullish(),
});
export type Connector = z.infer<typeof connectorSchema>;

// ── Typed config-form schema (from GET /connectors/types) ────────────────────

export const schemaFieldSchema = z.object({
  key: z.string(),
  label: z.string().nullish(),
  help: z.string().nullish(),
  type: z.string().nullish(), // text | number | password | textarea | select | boolean | code
  defaultValue: z.unknown().nullish(),
  required: z.boolean().nullish(),
  options: z.array(z.object({ value: z.string(), label: z.string().nullish() })).nullish(),
  placeholder: z.string().nullish(),
  visibleWhen: z.string().nullish(),
  language: z.string().nullish(),
});
export type SchemaField = z.infer<typeof schemaFieldSchema>;

export const configSchemaSchema = z.object({
  groups: z
    .array(
      z.object({
        title: z.string().nullish(),
        description: z.string().nullish(),
        fields: z.array(schemaFieldSchema).nullish(),
      }),
    )
    .nullish(),
});
export type ConfigFormSchema = z.infer<typeof configSchemaSchema>;

export const connectorTypeSchema = z.object({
  type: z.string(),
  displayName: z.string().nullish(),
  formSchema: configSchemaSchema.nullish(),
  secretFields: z.array(z.string()).nullish(),
  introspectable: z.boolean().nullish(),
});
export type ConnectorType = z.infer<typeof connectorTypeSchema>;

// ── Queries ─────────────────────────────────────────────────────────────────

/** Full unpaged list — for pickers and id→name maps. */
export const connectorsQuery = queryOptions({
  queryKey: ["connectors", "all"],
  queryFn: () =>
    apiRequest<unknown>("/connectors?all=true").then((r) => z.array(connectorSchema).parse(r)),
});

export function useConnectors() {
  return useQuery(connectorsQuery);
}

/** Server-paginated + filtered list for the connectors screen. */
export function useConnectorsPage(params: TableParamsApi) {
  const search = params.toSearchParams().toString();
  return useQuery({
    queryKey: ["connectors", "page", search],
    queryFn: () =>
      apiRequest<unknown>(`/connectors?${search}`).then((r) =>
        zSpringPage(connectorSchema).parse(r),
      ),
    placeholderData: keepPreviousData,
  });
}

export function connectorQuery(id: string) {
  return queryOptions({
    queryKey: ["connectors", "detail", id],
    queryFn: () => apiRequest<unknown>(`/connectors/${id}`).then((r) => connectorSchema.parse(r)),
  });
}
export function useConnector(id: string) {
  return useQuery(connectorQuery(id));
}

export function useConnectorNames(): Record<string, string> {
  const { data } = useConnectors();
  return useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of data ?? []) map[c.id] = c.name;
    return map;
  }, [data]);
}

export const connectorTypesQuery = queryOptions({
  queryKey: ["connectors", "types"],
  queryFn: () =>
    apiRequest<unknown>("/connectors/types").then((r) => z.array(connectorTypeSchema).parse(r)),
  staleTime: Number.POSITIVE_INFINITY,
});

export function useConnectorTypes() {
  return useQuery(connectorTypesQuery);
}

export function useConnectorTables(id: string, enabled = true) {
  return useQuery({
    queryKey: ["connectors", "tables", id],
    enabled,
    queryFn: () =>
      apiRequest<unknown>(`/connectors/${id}/tables`).then((r) =>
        z.array(z.record(z.string(), z.unknown())).parse(r),
      ),
  });
}

export function useConnectorColumns(id: string, table: string | null) {
  return useQuery({
    queryKey: ["connectors", "columns", id, table],
    enabled: Boolean(table),
    queryFn: () =>
      apiRequest<unknown>(
        `/connectors/${id}/columns?table=${encodeURIComponent(table ?? "")}`,
      ).then((r) => z.array(z.record(z.string(), z.unknown())).parse(r)),
  });
}

export interface QueryResult {
  rows: Record<string, unknown>[];
  rowCount: number;
  elapsedMs: number;
}

/** Ad-hoc query against a connector (not persisted). */
export function useConnectorTestQuery(id: string) {
  return useMutation({
    mutationFn: (payload: { query: string; limit?: number }) =>
      apiRequest<QueryResult>(`/connectors/${id}/test-query`, {
        method: "POST",
        body: { query: payload.query, limit: payload.limit ?? 50 },
      }),
  });
}

/** Dry-run: read a bounded sample of records straight from the source. */
export function useConnectorSample(id: string) {
  return useMutation({
    mutationFn: (limit: number) =>
      apiRequest<Record<string, unknown>[]>(`/connectors/${id}/sample?limit=${limit}`),
  });
}

// ── Mutations ───────────────────────────────────────────────────────────────

export interface ConnectorInput {
  name?: string;
  description?: string;
  type?: string;
  config?: Record<string, unknown>;
}

export function useConnectorMutations(id?: string) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["connectors"] });
  };

  return {
    create: useMutation({
      mutationFn: (body: ConnectorInput) =>
        apiRequest<Connector>("/connectors", { method: "POST", body }).then((r) =>
          connectorSchema.parse(r),
        ),
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: (body: ConnectorInput) =>
        apiRequest<Connector>(`/connectors/${id}`, { method: "PUT", body }).then((r) =>
          connectorSchema.parse(r),
        ),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (cid: string) => apiRequest<void>(`/connectors/${cid}`, { method: "DELETE" }),
      onSuccess: invalidate,
    }),
    test: useMutation({
      mutationFn: (cid: string) =>
        apiRequest<{ success?: boolean; message?: string }>(`/connectors/${cid}/test`, {
          method: "POST",
        }),
      onSettled: invalidate,
    }),
    moveWorkspace: useMutation({
      mutationFn: ({ cid, workspaceId }: { cid: string; workspaceId: string | null }) =>
        apiRequest<Connector>(`/connectors/${cid}/workspace`, {
          method: "PATCH",
          body: { workspaceId },
        }),
      onSuccess: invalidate,
    }),
    duplicate: useMutation({
      mutationFn: (cid: string) =>
        apiRequest<Connector>(`/connectors/${cid}/duplicate`, { method: "POST" }).then((r) =>
          connectorSchema.parse(r),
        ),
      onSuccess: invalidate,
    }),
    setArchived: useMutation({
      mutationFn: ({ cid, archived }: { cid: string; archived: boolean }) =>
        apiRequest<void>(`/connectors/${cid}/${archived ? "archive" : "unarchive"}`, {
          method: "PATCH",
        }),
      onSuccess: invalidate,
    }),
  };
}

export function useBulkConnectors() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      ids: string[];
      op: "archive" | "unarchive" | "delete" | "move";
      workspaceId?: string | null;
    }) =>
      apiRequest<{ ok: number; failed: { id: string; error: string }[] }>("/connectors/bulk", {
        method: "POST",
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["connectors"] }),
  });
}
